import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { DragIcon } from "components/icons/DragIcon";
import { BooksSettingLines } from "components/icons/Navbar/BooksSettingLines";
import { useLocalStorage } from "hooks/useLocalStorage";
import classNames from "classnames";
import { useCallback, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import {
  DEFAULT_REVENUE_METRICS_ORDER,
  DEFAULT_REVENUE_METRICS_VISIBLE,
  REVENUE_METRICS_SECTIONS,
  RevenueMetricsSection,
  RevenueMetricsSectionKey,
} from "./revenueMetricsCustomizeConstants";

const STORAGE_KEY = "revenue_metrics_visible_sections";
const SECTION_ORDER_KEY = "revenue_metrics_section_order";

const DRAG_TYPE = "REVENUE_METRICS_SECTION";

type DraggableSectionProps = {
  section: RevenueMetricsSection;
  index: number;
  visible: boolean;
  onToggle: (key: RevenueMetricsSectionKey) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
};

const DraggableSection = ({
  section,
  index,
  visible,
  onToggle,
  onMove,
}: DraggableSectionProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DRAG_TYPE,
      item: { index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [index]
  );

  const [{ isOver }, drop] = useDrop(
    () => ({
      accept: DRAG_TYPE,
      drop: (draggedItem: { index: number }) => {
        if (draggedItem.index !== index) {
          onMove(draggedItem.index, index);
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
      }),
    }),
    [index, onMove]
  );

  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={classNames(
        "t-flex t-items-center t-justify-between t-py-3 t-cursor-grab",
        {
          "!t-cursor-grabbing": isDragging,
          "t-opacity-100": !isDragging,
          "t-opacity-25": isOver,
        }
      )}
    >
      <Checkbox
        name={section.key}
        label={section.label}
        checked={visible}
        onChange={() => onToggle(section.key)}
      />
      <span className="t-text-neutral-40 t-select-none">
        <DragIcon />
      </span>
    </div>
  );
};

type CustomizeRevenueMetricsPanelProps = {
  open: boolean;
  onClose: () => void;
  draftVisible: Record<RevenueMetricsSectionKey, boolean>;
  draftOrder: RevenueMetricsSectionKey[];
  onToggle: (key: RevenueMetricsSectionKey) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  onReset: () => void;
  onCancel: () => void;
  onSave: () => void;
};

const CustomizeRevenueMetricsPanel = ({
  open,
  onClose,
  draftVisible,
  draftOrder,
  onToggle,
  onMove,
  onReset,
  onCancel,
  onSave,
}: CustomizeRevenueMetricsPanelProps) => {
  const orderedSections = draftOrder
    .map((key) => REVENUE_METRICS_SECTIONS.find((s) => s.key === key))
    .filter(Boolean) as RevenueMetricsSection[];

  return (
    <Modal.Root open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <Modal.Content size="regular">
        <Modal.Header>
          <Modal.Title>Customize Revenue Metrics Summary</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body className="t-flex t-flex-col t-gap-0">
          {orderedSections.map((section, index) => (
            <DraggableSection
              key={section.key}
              section={section}
              index={index}
              visible={draftVisible[section.key]}
              onToggle={onToggle}
              onMove={onMove}
            />
          ))}
        </Modal.Body>
        <Modal.Footer>
          <div className="t-flex t-items-center t-justify-between t-w-full">
            <Button customType="ghost" onClick={onReset}>
              Reset changes
            </Button>
            <div className="t-flex t-gap-2">
              <Button customType="secondary" onClick={onCancel}>
                Cancel
              </Button>
              <Button customType="primary" onClick={onSave}>
                Save
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};

export const useRevenueMetricsCustomize = () => {
  const { localData: visibleSections, setItem: setVisibleSections } =
    useLocalStorage<Record<RevenueMetricsSectionKey, boolean>>(
      STORAGE_KEY,
      DEFAULT_REVENUE_METRICS_VISIBLE
    );

  const { localData: sectionOrder, setItem: setSectionOrder } = useLocalStorage<
    RevenueMetricsSectionKey[]
  >(SECTION_ORDER_KEY, DEFAULT_REVENUE_METRICS_ORDER);

  return { visibleSections, setVisibleSections, sectionOrder, setSectionOrder };
};

export const CustomizeRevenueMetricsTrigger = ({
  visibleSections,
  sectionOrder,
  onSave,
}: {
  visibleSections: Record<RevenueMetricsSectionKey, boolean>;
  sectionOrder: RevenueMetricsSectionKey[];
  onSave: (
    newVisible: Record<RevenueMetricsSectionKey, boolean>,
    newOrder: RevenueMetricsSectionKey[]
  ) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftVisible, setDraftVisible] =
    useState<Record<RevenueMetricsSectionKey, boolean>>(visibleSections);
  const [draftOrder, setDraftOrder] =
    useState<RevenueMetricsSectionKey[]>(sectionOrder);

  const handleOpen = () => {
    setDraftVisible(visibleSections);
    setDraftOrder(sectionOrder);
    setIsOpen(true);
  };

  const handleToggle = (key: RevenueMetricsSectionKey) => {
    setDraftVisible((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleMove = useCallback((fromIndex: number, toIndex: number) => {
    setDraftOrder((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, []);

  const handleReset = () => {
    setDraftVisible(DEFAULT_REVENUE_METRICS_VISIBLE);
    setDraftOrder(DEFAULT_REVENUE_METRICS_ORDER);
  };

  const handleCancel = () => {
    setDraftVisible(visibleSections);
    setDraftOrder(sectionOrder);
    setIsOpen(false);
  };

  const handleSave = () => {
    onSave(draftVisible, draftOrder);
    setIsOpen(false);
  };

  return (
    <>
      <Button customType="secondary" size="small" onClick={handleOpen}>
        <span className="t-flex t-items-center t-gap-2">
          <BooksSettingLines />
          Customize
        </span>
      </Button>
      <CustomizeRevenueMetricsPanel
        open={isOpen}
        onClose={() => setIsOpen(false)}
        draftVisible={draftVisible}
        draftOrder={draftOrder}
        onToggle={handleToggle}
        onMove={handleMove}
        onReset={handleReset}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    </>
  );
};
